import React, { useState, useCallback, useImperativeHandle } from 'react'
import { Deposit } from './index'
import { OpenDepositOptions, DepositAPI } from '../types'
// constants
import { initDepositOptions } from '@/components/Deposit/src/constants'

// Global state for the deposit modal
let globalDepositManager: DepositAPI | null = null

export const DepositManager = React.forwardRef<DepositAPI>((_, ref) => {
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [options, setOptions] = useState<OpenDepositOptions>(initDepositOptions)

  const openDeposit = useCallback((newOptions: OpenDepositOptions) => {
    setOptions(newOptions)
    setIsOpen(true)
  }, [])

  // Function to update options while modal is open
  const updateDepositOptions = useCallback(
    (newOptions: Partial<OpenDepositOptions>) => {
      setOptions(prevOptions => ({ ...prevOptions, ...newOptions }))
    },
    []
  )

  const closeDeposit = useCallback(() => {
    setIsOpen(false)
    setIsLoading(false)
    // Call the custom onClose handler if provided
    if (options.onClose) {
      options.onClose()
    }
  }, [options.onClose])

  const setDepositLoading = useCallback((loading: boolean) => {
    setIsLoading(loading)
  }, [])

  const isDepositOpen = useCallback(() => {
    return isOpen
  }, [isOpen])

  const isDepositLoading = useCallback(() => {
    return isLoading
  }, [isLoading])

  // Expose API methods
  useImperativeHandle(
    ref,
    () => ({
      openDeposit,
      closeDeposit,
      updateDepositOptions,
      setDepositLoading,
      isOpen: isDepositOpen,
      isLoading: isDepositLoading,
    }),
    [
      openDeposit,
      closeDeposit,
      updateDepositOptions,
      setDepositLoading,
      isDepositOpen,
      isDepositLoading,
    ]
  )

  // Register global API
  React.useEffect(() => {
    globalDepositManager = {
      openDeposit,
      closeDeposit,
      updateDepositOptions,
      setDepositLoading,
      isOpen: isDepositOpen,
      isLoading: isDepositLoading,
    }

    return () => {
      globalDepositManager = null
    }
  }, [
    openDeposit,
    closeDeposit,
    updateDepositOptions,
    setDepositLoading,
    isDepositOpen,
    isDepositLoading,
  ])

  if (!isOpen) {
    return null
  }

  return (
    <Deposit
      isOpen={isOpen}
      isLoading={isLoading}
      onClose={closeDeposit}
      currentActiveAccount={options.currentActiveAccount}
      chains={options.chains}
      spotChains={options.spotChains}
      defaultChainId={options.defaultChainId}
      defaultAccountType={options.defaultAccountType}
      platformBalance={options.platformBalance}
      wagmiHooks={options.wagmiHooks}
      onDeposit={options.onDeposit}
      onCopy={options.onCopy}
      onChainChange={id => options.onChainChange?.(id)}
      onTokenChange={token => options.onTokenChange?.(token)}
    />
  )
})

DepositManager.displayName = 'DepositManager'

// Global API functions
export const openDeposit = (options?: OpenDepositOptions) => {
  if (!globalDepositManager) {
    console.error(
      'DepositManager not initialized. Make sure to render <DepositManager /> in your app.'
    )
    return
  }
  globalDepositManager.openDeposit(options || initDepositOptions)
}

export const closeDeposit = () => {
  if (!globalDepositManager) {
    console.error(
      'DepositManager not initialized. Make sure to render <DepositManager /> in your app.'
    )
    return
  }
  globalDepositManager.closeDeposit()
}

export const updateDepositOptions = (options: Partial<OpenDepositOptions>) => {
  if (!globalDepositManager) {
    return
  }
  globalDepositManager.updateDepositOptions(options)
}

export const setDepositLoading = (loading: boolean) => {
  if (!globalDepositManager) {
    console.error(
      'DepositManager not initialized. Make sure to render <DepositManager /> in your app.'
    )
    return
  }
  globalDepositManager.setDepositLoading(loading)
}

export const isDepositOpen = () => {
  if (!globalDepositManager) {
    return false
  }
  return globalDepositManager.isOpen()
}

export const isDepositLoading = () => {
  if (!globalDepositManager) {
    return false
  }
  return globalDepositManager.isLoading()
}

// Hook for using deposit API in React components
export const useDeposit = (): DepositAPI => {
  return {
    openDeposit,
    closeDeposit,
    updateDepositOptions,
    setDepositLoading,
    isOpen: isDepositOpen,
    isLoading: isDepositLoading,
  }
}
